import { Typography } from "@mui/material";
import { memo, useSyncExternalStore } from "react";

import { formatScoutWorkingElapsed } from "./formatScoutWorkingElapsed";

const TICK_MS = 1000;

let elapsedNowMs = Date.now();
let elapsedIntervalId: number | undefined;
const elapsedListeners = new Set<() => void>();

function emitElapsedNow(): void {
  for (const listener of elapsedListeners) {
    listener();
  }
}

function tickElapsedNow(): void {
  elapsedNowMs = Date.now();
  emitElapsedNow();
}

function subscribeElapsedNow(onStoreChange: () => void): () => void {
  elapsedListeners.add(onStoreChange);
  if (elapsedListeners.size === 1) {
    // A later turn must not keep the previous turn's last tick.
    elapsedNowMs = Date.now();
    elapsedIntervalId = window.setInterval(tickElapsedNow, TICK_MS);
  }
  onStoreChange();
  return () => {
    elapsedListeners.delete(onStoreChange);
    if (elapsedListeners.size === 0 && elapsedIntervalId !== undefined) {
      window.clearInterval(elapsedIntervalId);
      elapsedIntervalId = undefined;
    }
  };
}

function getElapsedNowSnapshot(): number {
  return elapsedNowMs;
}

/**
 * One shared one-second clock for every readout. A tick re-renders the
 * elapsed labels together, and not the message or composer around them.
 */
function useElapsedNow(): number {
  return useSyncExternalStore(subscribeElapsedNow, getElapsedNowSnapshot, getElapsedNowSnapshot);
}

/** Compact elapsed time (`37s`, `5m 52s`) in the same type as the status line. */
export const ScoutElapsedTime = memo(function ScoutElapsedTime({
  startedAtMs,
  variant = "body1",
}: {
  startedAtMs: number;
  variant?: "body1" | "caption";
}) {
  const now = useElapsedNow();

  return (
    <Typography
      component="span"
      variant={variant}
      color="inherit"
      sx={{ fontVariantNumeric: "tabular-nums" }}
    >
      {formatScoutWorkingElapsed(Math.max(0, now - startedAtMs))}
    </Typography>
  );
});

const workingStatusPulseSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
  pointerEvents: "none",
  "@keyframes scoutComposerWorkingPulse": {
    "0%, 100%": { opacity: 0.55 },
    "50%": { opacity: 1 },
  },
  animation: "scoutComposerWorkingPulse 1.6s ease-in-out infinite",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    opacity: 1,
  },
} as const;

export const ScoutWorkingStatus = memo(function ScoutWorkingStatus({
  startedAtMs,
  placement,
}: {
  startedAtMs: number;
  /** "beside" shares the button row. "below" sits under the button on a narrow composer. */
  placement: "beside" | "below";
}) {
  return (
    <Typography
      component="div"
      variant="caption"
      color="text.secondary"
      data-testid="scout-working-status"
      data-placement={placement}
      sx={{
        ...workingStatusPulseSx,
        ...(placement === "below"
          ? {
              mt: 0.5,
              px: 2,
              width: "100%",
              maxWidth: "100%",
            }
          : {
              position: "absolute",
              left: 0,
              px: 4,
              top: "82%",
              transform: "translateY(-50%)",
              maxWidth: "calc(50% - 28px)",
            }),
      }}
    >
      Scout has been working for <ScoutElapsedTime startedAtMs={startedAtMs} variant="caption" />
    </Typography>
  );
});
